/**
 * `readiness-cache` — stale-while-revalidate cache for the readiness
 * page's slow sources (public domain-readiness probe and install
 * coverage). Persists to `localStorage` keyed by site_id so a return
 * visit renders instantly from the last-known-good bundle, then
 * swaps in fresh data as it arrives.
 *
 * Discipline:
 *   - Every read / write is wrapped in try/catch. A storage failure
 *     (QuotaExceeded, private-mode denial, JSON parse error) must
 *     NEVER raise into the UI; the cache is additive.
 *   - Entries older than `MAX_AGE_MS` (7 days) are ignored.
 *   - Entries with a different shape — missing fields, wrong
 *     `version`, non-numeric `saved_at` — are ignored.
 *   - The storage key is namespaced + versioned
 *     (`sill:readiness-cache:v1:<site_id>`). Bumping `CACHE_VERSION`
 *     invalidates every entry at once.
 */

import type { InstallCoverage } from '@sill/types/api';

import type { ReadinessSignalsBundle } from '../../lib/domain-readiness.js';

const CACHE_VERSION = 1 as const;
const KEY_PREFIX = `sill:readiness-cache:v${CACHE_VERSION}:` as const;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface ReadinessCacheEntry {
  readonly version: typeof CACHE_VERSION;
  readonly site_id: string;
  readonly saved_at: number;
  readonly signals: ReadinessSignalsBundle | null;
  readonly install: InstallCoverage | null;
}

function key(site_id: string): string {
  return `${KEY_PREFIX}${site_id}`;
}

function storageAvailable(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    const s = window.localStorage;
    if (s === null || s === undefined) return null;
    return s;
  } catch {
    return null;
  }
}

/**
 * Read the cached bundle for a site. Returns `null` on ANY failure
 * (missing, expired, wrong shape, storage error).
 */
export function readReadinessCache(site_id: string): ReadinessCacheEntry | null {
  try {
    const s = storageAvailable();
    if (s === null) return null;
    const raw = s.getItem(key(site_id));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return null;
    const o = parsed as Record<string, unknown>;
    if (o['version'] !== CACHE_VERSION) return null;
    if (o['site_id'] !== site_id) return null;
    const saved_at = o['saved_at'];
    if (typeof saved_at !== 'number' || !Number.isFinite(saved_at)) return null;
    if (Date.now() - saved_at > MAX_AGE_MS) return null;
    const signals = o['signals'];
    const install = o['install'];
    // Both can be null; the UI treats a null slot the same as never-measured.
    const entry: ReadinessCacheEntry = {
      version: CACHE_VERSION,
      site_id,
      saved_at,
      signals: (signals === null || (typeof signals === 'object'))
        ? (signals as ReadinessSignalsBundle | null)
        : null,
      install: (install === null || (typeof install === 'object'))
        ? (install as InstallCoverage | null)
        : null,
    };
    // Treat an entry with BOTH slots null as absent — nothing useful to seed.
    if (entry.signals === null && entry.install === null) return null;
    return entry;
  } catch {
    return null;
  }
}

/**
 * Persist the latest successful signals + install bundle. Called when
 * each source resolves with a non-null value. Failures are silent.
 */
export function writeReadinessCache(
  site_id: string,
  patch: { readonly signals?: ReadinessSignalsBundle | null; readonly install?: InstallCoverage | null },
): void {
  try {
    const s = storageAvailable();
    if (s === null) return;
    const prior = readReadinessCache(site_id);
    const next: ReadinessCacheEntry = {
      version: CACHE_VERSION,
      site_id,
      saved_at: Date.now(),
      signals: patch.signals !== undefined ? patch.signals : (prior?.signals ?? null),
      install: patch.install !== undefined ? patch.install : (prior?.install ?? null),
    };
    s.setItem(key(site_id), JSON.stringify(next));
  } catch {
    /* silent — cache is additive */
  }
}

/** Test-only clear for a site. */
export function clearReadinessCache(site_id: string): void {
  try {
    const s = storageAvailable();
    if (s === null) return;
    s.removeItem(key(site_id));
  } catch {
    /* ignore */
  }
}

export const __readinessCacheInternals = {
  KEY_PREFIX,
  CACHE_VERSION,
  MAX_AGE_MS,
};
